// FLEET LIVE's data hook (#6778): first paint reads `GET /v1/fleet/live`, then the
// `fleet_live` topic of the shell's ONE account stream patches it (`useStreamTopics`; no
// second socket). The fold is `mergeFleetLive` (`fleet-live-frames.ts`), by presence: an
// absent topic renders the REST body, a served topic is the truth.
//
// The REST read polls at 30 s only while the stream is not live (the `useCiRuns` posture),
// so an api pod recycle falls back to the read and the stream's re-snapshot heals the board:
// nothing here keeps its own bookkeeping of what changed.

import { type FleetLiveWire, FleetLiveWireSchema } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { createMemo } from 'solid-js';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { FLEET_LIVE_TOPIC, mergeFleetLive } from './fleet-live-frames';
import { useStreamTopics } from './stream-topics';

export const FLEET_LIVE_QUERY_KEY = 'fleet_live';

/** The REST read's poll while the stream is down; off while it is live. */
export const FLEET_LIVE_POLL_MS = 30_000;

export type FleetLiveRead = {
  readonly query: {
    readonly isLoading: boolean;
    readonly isError: boolean;
    readonly error: unknown;
    readonly refetch: () => unknown;
  };
  /** The body to draw, REST and stream folded; `undefined` before either has arrived. */
  readonly wire: () => FleetLiveWire | undefined;
};

export function useFleetLive(token: string | null): FleetLiveRead {
  const stream = useStreamTopics([FLEET_LIVE_TOPIC], token);
  const live = (): boolean => stream.status() === 'live';
  const query = createQuery<FleetLiveWire>(() => ({
    queryKey: [FLEET_LIVE_QUERY_KEY, token, activeAccountId()],
    enabled: token !== null,
    refetchInterval: live() ? false : FLEET_LIVE_POLL_MS,
    queryFn: () => apiFetch('/v1/fleet/live', token, undefined, { schema: FleetLiveWireSchema }),
  }));
  const wire = createMemo(() => mergeFleetLive(query.data, stream.store[FLEET_LIVE_TOPIC]));
  return { query, wire };
}
